/**
 * Reset password — Round 46 (Sep 30 2026).
 *
 * The reset email has always linked to gemslive.tv/reset-password?token=…,
 * but no page lived there: the site sent that address to the home page and
 * the token was lost, so nobody could finish a reset. This is that page.
 *
 * It stands alone (outside /app and its sign-in gate): the person arrives
 * from an email, usually on their phone, and only needs to type a new
 * password. Afterwards they go back to the GEMS app — or sign in on the web.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { BRAND_ASSETS } from '../brandAssets';

const MIN_LENGTH = 6;   // the server's rule

const field = {
  width: '100%', boxSizing: 'border-box', padding: '13px 14px', borderRadius: 12, fontSize: 16,
  background: '#0b1210', color: '#f4fbf7', border: '1px solid rgba(255,255,255,0.14)', outline: 'none', marginTop: 6,
};

const ResetPasswordPage = () => {
  const token = new URLSearchParams(window.location.search).get('token') || '';
  const [password, setPassword] = useState('');
  const [again, setAgain] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (password.length < MIN_LENGTH) { setError(`Use at least ${MIN_LENGTH} characters.`); return; }
    if (password !== again) { setError('The two passwords do not match.'); return; }
    setBusy(true); setError('');
    try {
      await axios.post(`${API_BASE_URL}/api/auth/reset-password`, { token, new_password: password });
      setDone(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' && /expired|invalid/i.test(detail)
        ? 'This link has expired or was already used. Ask for a new one from the sign-in screen.'
        : (typeof detail === 'string' ? detail : 'Could not change the password. Please try again.'));
    } finally { setBusy(false); }
  };

  return (
    <div
      data-testid="reset-password-page"
      style={{ minHeight: '100vh', background: '#07100c', color: '#f4fbf7', display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16, boxSizing: 'border-box', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}
    >
      <div style={{ width: '100%', maxWidth: 420, background: '#0f1a15', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 18, padding: 26 }}>
        <div style={{ textAlign: 'center', marginBottom: 18 }}>
          <img src={BRAND_ASSETS.logo} alt="GEMS" style={{ width: 64, height: 64, objectFit: 'contain', display: 'block', margin: '0 auto' }} />
          <h1 style={{ fontSize: 22, fontWeight: 800, margin: '10px 0 4px' }}>{done ? 'Password changed' : 'Choose a new password'}</h1>
          <p style={{ color: '#9db3a8', fontSize: 14, margin: 0 }}>
            {done ? 'You can sign in with your new password now.' : 'For your GEMS account.'}
          </p>
        </div>

        {!token && !done && (
          <p style={{ color: '#fbbf24', fontSize: 14, lineHeight: 1.5 }} data-testid="reset-no-token">
            This link is missing its code. Open the link from your email again, or ask for a new one from the sign-in screen.
          </p>
        )}

        {token && !done && (
          <form onSubmit={submit}>
            <label style={{ fontSize: 13, color: '#c7d6ce' }}>New password
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password"
                style={field} data-testid="reset-new-password" />
            </label>
            <label style={{ fontSize: 13, color: '#c7d6ce', display: 'block', marginTop: 12 }}>Type it again
              <input type="password" value={again} onChange={(e) => setAgain(e.target.value)} autoComplete="new-password"
                style={field} data-testid="reset-confirm-password" />
            </label>
            {error && <p style={{ color: '#fb7185', fontSize: 13.5, marginTop: 12 }} data-testid="reset-error">{error}</p>}
            <button type="submit" disabled={busy} data-testid="reset-submit"
              style={{ width: '100%', marginTop: 18, padding: '14px 18px', border: 0, borderRadius: 12, background: '#10b981', color: '#04120c',
                fontWeight: 800, fontSize: 16, cursor: 'pointer', opacity: busy ? 0.6 : 1 }}>
              {busy ? 'Saving…' : 'Save new password'}
            </button>
          </form>
        )}

        {done && (
          <div style={{ textAlign: 'center' }} data-testid="reset-done">
            <p style={{ color: '#c7d6ce', fontSize: 14, lineHeight: 1.5 }}>Open the GEMS app and sign in with your email and new password.</p>
            <a href="/get-app" style={{ display: 'inline-block', marginTop: 8, padding: '12px 22px', borderRadius: 999, background: '#10b981', color: '#04120c', fontWeight: 800, textDecoration: 'none' }}>
              Get the app
            </a>
            <p style={{ marginTop: 14, fontSize: 13 }}>
              <a href="/beta-access" style={{ color: '#34d399' }}>Or sign in on the web</a>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResetPasswordPage;
